import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Clock, Send, Shield, ChevronDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { validateEmail, validateName, validatePhoneNumber, sanitizePhoneInput } from '../utils/validation';
import { countryCodes } from '../mock';
import { getGeolocation, submitLead, getDialCodeFromCountryCode } from '../services/apiService';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeolocation = async () => {
      const geoData = await getGeolocation();
      setGeoLocationData(geoData);
      
      if (geoData.success && geoData.countryCode) {
        const dialCode = getDialCodeFromCountryCode(geoData.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode,
          country: geoData.countryCode
        }));
      }
    };

    fetchGeolocation();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let sanitizedValue = value;
    
    // Sanitize phone input - only allow digits
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneInput(value);
      // Limit to 10 digits
      if (sanitizedValue.length > 10) {
        sanitizedValue = sanitizedValue.slice(0, 10);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    let newErrors = {
      name: '',
      email: '',
      phone: '',
      message: ''
    };
    
    let hasErrors = false;
    
    // Validate name
    if (!validateName(formData.name)) {
      newErrors.name = 'Name must be at least 2 characters long';
      hasErrors = true;
    }
    
    // Validate email - use proper object return
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
      hasErrors = true;
    }
    
    // Validate phone - must be exactly 10 digits
    if (!formData.phone || !validatePhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number (e.g., 1234567890)';
      hasErrors = true;
    }
    
    // Validate message
    if (formData.message && formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setErrors(newErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form before submitting.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for API
    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryCode || '+1', // Default to +1 if not set
      country: formData.country || 'US', // Default to US if not set
      phoneNumber: formData.phone, // Only numeric
      description: formData.message
    };

    console.log('Submitting contact lead data:', leadData);

    // Submit to CRM API
    const result = await submitLead(leadData, geoLocationData);

    setIsSubmitting(false);

    if (result.success) {
      console.log('Contact form submission successful:', result);
      console.log('Redirect URL:', result.redirectUrl);
      console.log('Customer ID:', result.customerId);
      
      toast({
        title: "Success!",
        description: "Your message has been sent successfully. Redirecting..."
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        country: 'US',
        phone: '',
        message: ''
      });
      setErrors({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Redirect to application page
      if (result.redirectUrl) {
        setTimeout(() => {
          console.log('Executing redirect to:', result.redirectUrl);
          window.location.href = result.redirectUrl;
        }, 1500);
      } else {
        console.error('No redirect URL in result:', result);
        toast({
          title: "Error",
          description: "Redirect URL not found. Please contact support.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to submit. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#fd5a27]/20 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-[#fd5a27]/20 mb-4 lg:mb-6">
            <Shield className="w-4 h-4 lg:w-5 lg:h-5 text-[#fd5a27]" />
            <span className="text-xs sm:text-sm font-medium text-gray-200">24/7 Support Available</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 lg:mb-6">
            Get In Touch
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto">
            Have questions about trademark registration? Our expert team is here to help you protect your brand.
          </p>
        </div>
      </section>

      {/* Contact Section - Form First on Mobile */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Contact Form - First on Mobile, Second on Desktop */}
            <div className="order-1 lg:order-2 bg-slate-800/50 p-6 lg:p-8 rounded-xl border border-slate-700 hover:border-[#fd5a27]/30 transition-all duration-300">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Send Us a Message
              </h2>
              <p className="text-sm sm:text-base text-gray-400 mb-6">We'll get back to you within 24 hours</p>
              
              <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-[#fd5a27] transition-colors ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.name}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-[#fd5a27] transition-colors ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.email}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-gray-300">Contact Number *</Label>
                  <div className="mt-2 flex gap-2">
                    {/* Country Code Dropdown */}
                    <div className="relative">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        className="h-full px-3 py-2 bg-slate-900/50 border border-slate-600 text-white rounded-md appearance-none cursor-pointer hover:bg-slate-800 focus:border-[#fd5a27] focus:outline-none transition-colors pr-8"
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    
                    {/* Phone Number Input */}
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className={`flex-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-[#fd5a27] transition-colors ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-400 text-sm mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.phone}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="message" className="text-gray-300">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`mt-2 min-h-[150px] bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-[#fd5a27] transition-colors ${errors.message ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {errors.message && (
                    <p className="text-red-400 text-sm mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.message}
                    </p>
                  )}
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Contact Information - Second on Mobile, First on Desktop */}
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 lg:mb-6">
                Contact Information
              </h2>
              <p className="text-sm sm:text-base text-gray-300 mb-6 lg:mb-8">
                Reach out to us through any of the following channels. Our team is available 24/7 to assist you with your trademark registration needs.
              </p>
              
              <div className="space-y-4 lg:space-y-6">
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50 group">
                  <div className="w-12 h-12 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Phone className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-bold text-white mb-1 group-hover:text-[#fd5a27] transition-colors duration-300">Phone</h3>
                    <p className="text-sm lg:text-base text-gray-300">+1 (888) 430-8390</p>
                  </div>
                </div>
                
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50 group">
                  <div className="w-12 h-12 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Mail className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-bold text-white mb-1 group-hover:text-[#fd5a27] transition-colors duration-300">Email</h3>
                    <p className="text-sm lg:text-base text-gray-300">info@trademarkipus.com</p>
                  </div>
                </div>
                
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50 group">
                  <div className="w-12 h-12 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <MapPin className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-bold text-white mb-1 group-hover:text-[#fd5a27] transition-colors duration-300">Office Address</h3>
                    <p className="text-sm lg:text-base text-gray-300">
                      3800 Barham Blvd, Los Angeles, CA 90068
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 hover:border-[#fd5a27]/50 transition-all duration-300 hover:bg-slate-800/50 group">
                  <div className="w-12 h-12 bg-[#fd5a27]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#fd5a27]/20 transition-all duration-300">
                    <Clock className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-bold text-white mb-1 group-hover:text-[#fd5a27] transition-colors duration-300">Business Hours</h3>
                    <p className="text-sm lg:text-base text-gray-300">
                      24/7 Customer Support<br />
                      Live Chat Available<br />
                      Response within 24 hours
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 lg:mt-8 p-4 lg:p-6 bg-gradient-to-br from-[#fd5a27]/30 via-[#e04a1f]/30 to-[#d0421a]/30 rounded-xl border border-[#fd5a27]/30 hover:border-[#fd5a27]/50 transition-all duration-300">
                <h3 className="text-base lg:text-lg font-bold text-white mb-2">Need Immediate Assistance?</h3>
                <p className="text-sm lg:text-base text-gray-300 mb-4">
                  Our live chat support is available 24/7 to answer your questions instantly.
                </p>
                <Button className="chat bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white w-full transition-all duration-300">
                  Start Live Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
